import LanguageIcon from '@mui/icons-material/Language';
import { alpha, Box, IconButton, useMediaQuery, useTheme } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getAssetsBaseUrl } from '../../../config/assetsUrl';
import { socialMediaIconUrls } from '../../../config/socialMediaIcons';
import AnimatedTypography from '../../core/AnimatedTypography';

export interface SponsorLink {
    instagram?: string;
    facebook?: string;
    youtube?: string;
    website?: string;
}

export interface SponsorItem {
    src: string;
    alt: string;
    instagram?: string;
    facebook?: string;
    youtube?: string;
    website?: string;
}

const SPONSOR_IMAGES: SponsorItem[] = [
    {
        src: 'motel_radusa1.jpeg',
        alt: 'Motel Raduša',
        instagram: "https://www.instagram.com/motel_restaurant_radusa/",
        facebook: "https://www.facebook.com/motelradusa"
    },
    {
        src: 'shooting1.jpeg',
        alt: 'Shooting',
        instagram: 'https://www.instagram.com/shooting_range_one/'
    },
    {
        src: 'gap_motorsport.jpeg',
        alt: 'Gap Motorsport',
        instagram: "https://www.instagram.com/gap_motosport/",
        website: "https://www.gapromotion.com/"
    },
    {
        src: 'grip.jpeg',
        alt: 'XGrip',
        instagram: "https://www.instagram.com/x_grip/",
        website: "https://www.x-grip.at/",
        facebook: "https://www.facebook.com/xgripmousse/"
    },
];

const Sponsors: React.FC = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
    const baseUrl = getAssetsBaseUrl();
    const [openSponsorKey, setOpenSponsorKey] = useState<string | null>(null);

    const handleSponsorClick = (key: string) => {
        if (!isDesktop) {
            setOpenSponsorKey((prev) => (prev === key ? null : key));
        }
    };

    const handleLinkClick = (url: string) => {
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    return (
        <Box sx={{ py: 5, px: 0, pb: 3 }}>
            <AnimatedTypography variant="h3" color="text2.primary" fontWeight={600} mb={3}>
                {t('home.sponsorsTitle')}
            </AnimatedTypography>
            <Box
                display="flex"
                flexDirection="row"
                flexWrap="wrap"
                alignItems="center"
                justifyContent="flex-start"
                gap={{ xs: 2, md: 4 }}
            >
                {SPONSOR_IMAGES.map((sponsor) => {
                    const key = sponsor.src;
                    const hasLinks =
                        sponsor.instagram || sponsor.facebook || sponsor.youtube || sponsor.website;
                    const isOverlayOpen = isDesktop ? false : openSponsorKey === key;
                    const showOverlay = isDesktop ? undefined : isOverlayOpen;

                    return (
                        <Box
                            key={key}
                            onClick={() => hasLinks && handleSponsorClick(key)}
                            onMouseEnter={isDesktop && hasLinks ? () => { } : undefined}
                            onMouseLeave={isDesktop && hasLinks ? () => { } : undefined}
                            sx={{
                                width: isDesktop ? 240 : 'calc(50% - 8px)',
                                height: isDesktop ? 180 : 'auto',
                                flexShrink: 0,
                                borderRadius: 3,
                                overflow: 'hidden',
                                position: 'relative',
                                cursor: hasLinks ? (isDesktop ? 'pointer' : 'pointer') : 'default',
                                transition: 'transform 0.25s ease',
                                WebkitTapHighlightColor: 'transparent',
                                outline: 'none',
                                '&:focus': { outline: 'none' },
                                ...(isDesktop && {
                                    '&:hover': {
                                        transform: 'scale(1.06)',
                                    },
                                    '&:hover .sponsor-overlay': {
                                        opacity: hasLinks ? 1 : 0,
                                        pointerEvents: hasLinks ? 'auto' : 'none',
                                    },
                                }),
                            }}
                        >
                            <Box
                                component="img"
                                src={`${baseUrl}/images/sponsors/${sponsor.src}`}
                                alt={sponsor.alt}
                                sx={{
                                    width: '100%',
                                    height: isDesktop ? '100%' : 'auto',
                                    objectFit: 'contain',
                                    display: 'block',
                                    opacity: 0.9,
                                }}
                            />
                            {hasLinks && (
                                <Box
                                    className="sponsor-overlay"
                                    onClick={() => !isDesktop && setOpenSponsorKey((prev) => (prev === key ? null : prev))}
                                    sx={{
                                        position: 'absolute',
                                        inset: 0,
                                        backgroundColor: alpha(theme.palette.common.black, 0.65),
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 2,
                                        opacity: showOverlay ? 1 : 0,
                                        transition: 'opacity 0.25s ease',
                                        pointerEvents: isDesktop ? 'none' : showOverlay ? 'auto' : 'none',
                                        WebkitTapHighlightColor: 'transparent',
                                        outline: 'none',
                                    }}
                                >
                                    <Box className="sponsor-overlay-icons" sx={{ position: 'relative', zIndex: 1, display: 'flex', gap: { xs: "4px", sm: 2 }, alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
                                        {sponsor.instagram && (
                                            <IconButton
                                                size="large"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleLinkClick(sponsor.instagram!);
                                                }}
                                                aria-label="Instagram"
                                                sx={(t) => ({
                                                    width: { xs: 52, sm: 56 },
                                                    height: { xs: 52, sm: 56 },
                                                    backgroundColor: alpha(t.palette.background.paper, 0.85),
                                                    border: '2px solid #E4405F',
                                                    '&:hover': { backgroundColor: alpha(t.palette.background.paper, 0.6) },
                                                })}
                                            >
                                                <Box component="img" src={socialMediaIconUrls.instagram()} alt="" sx={{ width: { xs: 28, sm: 32 }, height: { xs: 28, sm: 32 }, objectFit: 'contain' }} />
                                            </IconButton>
                                        )}
                                        {sponsor.facebook && (
                                            <IconButton
                                                size="large"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleLinkClick(sponsor.facebook!);
                                                }}
                                                aria-label="Facebook"
                                                sx={(t) => ({
                                                    width: { xs: 52, sm: 56 },
                                                    height: { xs: 52, sm: 56 },
                                                    backgroundColor: alpha(t.palette.background.paper, 0.85),
                                                    border: '2px solid #1877F2',
                                                    '&:hover': { backgroundColor: alpha(t.palette.background.paper, 0.6) },
                                                })}
                                            >
                                                <Box component="img" src={socialMediaIconUrls.facebook()} alt="" sx={{ width: { xs: 28, sm: 32 }, height: { xs: 28, sm: 32 }, objectFit: 'contain' }} />
                                            </IconButton>
                                        )}
                                        {sponsor.youtube && (
                                            <IconButton
                                                size="large"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleLinkClick(sponsor.youtube!);
                                                }}
                                                aria-label="YouTube"
                                                sx={(t) => ({
                                                    width: { xs: 52, sm: 56 },
                                                    height: { xs: 52, sm: 56 },
                                                    backgroundColor: alpha(t.palette.background.paper, 0.85),
                                                    border: '2px solid #FF0000',
                                                    '&:hover': { backgroundColor: alpha(t.palette.background.paper, 0.6) },
                                                })}
                                            >
                                                <Box component="img" src={socialMediaIconUrls.youtube()} alt="" sx={{ width: { xs: 28, sm: 32 }, height: { xs: 28, sm: 32 }, objectFit: 'contain' }} />
                                            </IconButton>
                                        )}
                                        {sponsor.website && (
                                            <IconButton
                                                size="large"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleLinkClick(sponsor.website!);
                                                }}
                                                aria-label="Website"
                                                sx={(t) => ({
                                                    width: { xs: 52, sm: 56 },
                                                    height: { xs: 52, sm: 56 },
                                                    backgroundColor: alpha(t.palette.background.paper, 0.85),
                                                    border: `2px solid ${t.palette.grey[500]}`,
                                                    color: t.palette.text.primary,
                                                    '& .MuiSvgIcon-root': { fontSize: 32 },
                                                    '&:hover': { backgroundColor: alpha(t.palette.background.paper, 0.6) },
                                                })}
                                            >
                                                <LanguageIcon />
                                            </IconButton>
                                        )}
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
};

export default Sponsors;
